import { dateToDateOnlyApiFormat, formatUtcCalendarDate } from '@/helpers/dateHelper';
import { TemplateOption } from '@/types/template.types';
import { format } from 'date-fns';

/*
 * The roster calendar's selected month is a UTC-midnight date, while the date picker
 * and everything on the optimiser page work in local-midnight dates.
 */

export const isPastRosterMonth = (selectedMonth: Date, now: Date) =>
  formatUtcCalendarDate(selectedMonth, 'yyyy-MM') < format(now, 'yyyy-MM');

export const getOptimiserDateBounds = (selectedMonth: Date, now: Date) => {
  const year = selectedMonth.getUTCFullYear();
  const month = selectedMonth.getUTCMonth();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  return {
    minStartDate: isCurrentMonth ? today : new Date(year, month, 1),
    defaultEndDate: new Date(year, month + 1, 0)
  };
};

export interface OptimiserSelection {
  date: Date;
  templateId?: string;
}

export const buildRequestedDays = (
  startDate: Date,
  endDate: Date,
  currentRequests: OptimiserSelection[],
  templateOptions: TemplateOption[]
) => {
  /*
   * The requested days array needs to be set up whenever start and end date change.
   *
   * This function creates the array, keeping any data from the previous version of the array.
   */
  const newRequests: OptimiserSelection[] = [];
  const dateToAdd = new Date(startDate);

  // Take the first option which is classed as an acceptable default. Trust the template page not to create multiple defaults.
  const defaultOption = templateOptions.find(t => t.default);

  // Walk through the dates requested.
  while (dateToDateOnlyApiFormat(dateToAdd) <= dateToDateOnlyApiFormat(endDate)) {
    const newRequest: OptimiserSelection = {
      date: new Date(dateToAdd)
    };

    const existingRequest = currentRequests.find(
      r => dateToDateOnlyApiFormat(r.date) === dateToDateOnlyApiFormat(dateToAdd)
    );

    if (existingRequest) {
      // If there's an existing request, respect that.
      newRequest.templateId = existingRequest.templateId;
    } else {
      // Fall back to the default.
      newRequest.templateId = defaultOption?.id;
    }

    newRequests.push(newRequest);

    dateToAdd.setDate(dateToAdd.getDate() + 1);
  }

  return newRequests;
};

// Requested days are local-midnight dates from the date picker.
export const getOptimiserRequestRange = (requestedDays: { date: Date }[]) => {
  const startDate = new Date(Math.min(...requestedDays.map(rd => rd.date.getTime())));
  const endDate = new Date(Math.max(...requestedDays.map(rd => rd.date.getTime())));

  return { Start: dateToDateOnlyApiFormat(startDate), End: dateToDateOnlyApiFormat(endDate) };
};
